import { Plugin, PluginManager, PluginContext, PluginRegistry, Logger, EventEmitter } from './types'

export class KitiumPluginManager implements PluginManager {
  private plugins: Map<string, Plugin> = new Map()
  private registry: PluginRegistry = {
    storage: new Map(),
    billing: new Map(),
    framework: new Map(),
    'auth-provider': new Map(),
    custom: new Map()
  }
  private context: PluginContext
  private initialized = false

  constructor(context: PluginContext) {
    this.context = context
  }

  async register(plugin: Plugin): Promise<void> {
    if (this.plugins.has(plugin.name)) {
      throw new Error(`Plugin '${plugin.name}' is already registered`)
    }

    // Check dependencies
    if (plugin.dependencies) {
      for (const dep of plugin.dependencies) {
        if (!this.plugins.has(dep)) {
          throw new Error(`Plugin '${plugin.name}' requires dependency '${dep}' which is not registered`)
        }
      }
    }

    // Register plugin
    this.plugins.set(plugin.name, plugin)
    
    // Add to appropriate registry
    if ('type' in plugin) {
      const type = (plugin as any).type
      if (this.registry[type as keyof PluginRegistry]) {
        this.registry[type as keyof PluginRegistry].set(plugin.name, plugin as any)
      }
    } else {
      this.registry.custom.set(plugin.name, plugin)
    }

    this.context.logger.info(`Plugin '${plugin.name}' registered successfully`)

    // Initialize if already initialized
    if (this.initialized && plugin.initialize) {
      try {
        await plugin.initialize(this.context)
        this.context.logger.info(`Plugin '${plugin.name}' initialized`)
      } catch (error) {
        this.context.logger.error(`Failed to initialize plugin '${plugin.name}':`, error)
        throw error
      }
    }
  }

  async unregister(name: string): Promise<void> {
    const plugin = this.plugins.get(name)
    if (!plugin) {
      throw new Error(`Plugin '${name}' is not registered`)
    }

    // Check if other plugins depend on this one
    for (const [pluginName, p] of this.plugins) {
      if (p.dependencies?.includes(name)) {
        throw new Error(`Cannot unregister plugin '${name}' because '${pluginName}' depends on it`)
      }
    }

    // Destroy plugin
    if (plugin.destroy) {
      try {
        await plugin.destroy()
        this.context.logger.info(`Plugin '${name}' destroyed`)
      } catch (error) {
        this.context.logger.error(`Failed to destroy plugin '${name}':`, error)
      }
    }

    // Remove from registries
    this.plugins.delete(name)
    for (const registry of Object.values(this.registry)) {
      registry.delete(name)
    }

    this.context.logger.info(`Plugin '${name}' unregistered`)
  }

  get(name: string): Plugin | undefined {
    return this.plugins.get(name)
  }

  list(): Plugin[] {
    return Array.from(this.plugins.values())
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      return
    }

    // Initialize plugins in dependency order
    const sortedPlugins = this.topologicalSort()
    
    for (const plugin of sortedPlugins) {
      if (plugin.initialize) {
        try {
          await plugin.initialize(this.context)
          this.context.logger.info(`Plugin '${plugin.name}' initialized`)
        } catch (error) {
          this.context.logger.error(`Failed to initialize plugin '${plugin.name}':`, error)
          throw error
        }
      }
    }

    this.initialized = true
    this.context.logger.info('All plugins initialized successfully')
  }

  async destroy(): Promise<void> {
    if (!this.initialized) {
      return
    }

    // Destroy plugins in reverse dependency order
    const sortedPlugins = this.topologicalSort().reverse()
    
    for (const plugin of sortedPlugins) {
      if (plugin.destroy) {
        try {
          await plugin.destroy()
          this.context.logger.info(`Plugin '${plugin.name}' destroyed`)
        } catch (error) {
          this.context.logger.error(`Failed to destroy plugin '${plugin.name}':`, error)
        }
      }
    }

    this.initialized = false
    this.context.logger.info('All plugins destroyed')
  }

  // Get plugins by type
  getStoragePlugins() {
    return Array.from(this.registry.storage.values())
  }

  getBillingPlugins() {
    return Array.from(this.registry.billing.values())
  }

  getFrameworkPlugins() {
    return Array.from(this.registry.framework.values())
  }

  getAuthProviderPlugins() {
    return Array.from(this.registry['auth-provider'].values())
  }

  getCustomPlugins() {
    return Array.from(this.registry.custom.values())
  }

  // Topological sort for dependency resolution
  private topologicalSort(): Plugin[] {
    const visited = new Set<string>()
    const visiting = new Set<string>()
    const result: Plugin[] = []

    const visit = (pluginName: string) => {
      if (visiting.has(pluginName)) {
        throw new Error(`Circular dependency detected involving plugin '${pluginName}'`)
      }
      if (visited.has(pluginName)) {
        return
      }

      visiting.add(pluginName)
      const plugin = this.plugins.get(pluginName)
      if (plugin?.dependencies) {
        for (const dep of plugin.dependencies) {
          visit(dep)
        }
      }
      visiting.delete(pluginName)
      visited.add(pluginName)
      result.push(plugin!)
    }

    for (const pluginName of this.plugins.keys()) {
      if (!visited.has(pluginName)) {
        visit(pluginName)
      }
    }

    return result
  }
}
